/**
 * Aritmetica da meta calculada por media historica.
 *
 * Regra acordada com o negocio:
 *   - meses sem realizado NAO entram no divisor (ferias, afastamento)
 *   - meta = teto(media * (1 + percentual/100))
 *   - meta nunca fica abaixo do piso informado
 */

/**
 * Media das quantidades, considerando apenas os meses com realizado > 0.
 * @param {Array<{quantidade: number|string}>} meses
 * @returns {number} media, ou 0 se nenhum mes teve realizado
 */
export function calcularMedia(meses) {
  const comRealizado = (meses || []).filter(m => (parseFloat(m.quantidade) || 0) > 0);
  if (comRealizado.length === 0) return 0;
  const soma = comRealizado.reduce((s, m) => s + (parseFloat(m.quantidade) || 0), 0);
  return soma / comRealizado.length;
}

/**
 * Casas decimais usadas para normalizar o resultado do crescimento antes do
 * teto. Ponto flutuante binario nao representa exatamente valores como
 * 100 * 1.10 (vira 110.00000000000001) e o Math.ceil puro infla a meta em 1
 * unidade nesses casos. Arredondar para 8 casas antes do teto elimina o
 * ruido de ponto flutuante sem afetar nenhum caso de negocio real.
 */
const CASAS_NORMALIZACAO = 8;

/**
 * Crescimento aplicado sobre a media, arredondado para cima, sem considerar
 * o piso. Usada por calcularMetasColaboradores antes de aplicar o piso
 * (tambem arredondado para cima, ver pisoInteiro la embaixo).
 * @param {number} media
 * @param {number} percentual
 * @returns {number}
 */
function metaComCrescimentoSemPiso(media, percentual) {
  const bruta = (parseFloat(media) || 0) * (1 + percentual / 100);
  const normalizada = Number(bruta.toFixed(CASAS_NORMALIZACAO));
  return Math.ceil(normalizada);
}

/**
 * Resolve o percentual de crescimento aplicavel a uma media.
 *
 * Regra corrigida com o negocio em 2026-09-10: o "ate" de cada faixa e so
 * ROTULO (numero redondo pra comunicacao — "8 a 9", "10 a 15"), NAO o limite
 * matematico. Quem decide ate onde uma faixa vale de verdade e o "de" da
 * PROXIMA faixa, nao o proprio "ate". Por isso o calculo ignora "ate"
 * completamente: pega, entre as faixas cujo "de" e <= a media, a de MAIOR
 * "de" — essa e a que vale. Equivale a "a ultima faixa que a media ja
 * alcancou". A de maior "de" de todas nunca tem teto (nada e maior que ela).
 *
 * A primeira faixa (menor "de") comeca exatamente no piso — a validacao de
 * cadastro (faixasCrescimentoUtils.js) garante isso antes de qualquer meta
 * ser calculada, entao "media abaixo de toda faixa" nao e um caso a tratar
 * aqui: se acontecer apesar da validacao, e bug de cadastro.
 *
 * Recebe as faixas no formato INTERNO do form ({de, ate, crescimento} —
 * mesmo shape que ModalFaixasCrescimento edita e que form.faixasCrescimento
 * guarda), nao no formato do payload da API ({faixa_de, faixa_ate,
 * percentual_crescimento}). A traducao entre os dois shapes e
 * responsabilidade so da borda do payload (payloadMetaExtras.js na saida,
 * ModalCadastroMeta.editarMeta na entrada) — aqui dentro e sempre o shape do
 * form.
 * @param {number} media
 * @param {Array<{de:number, ate:?number, crescimento:number}>} faixas
 * @returns {?number} percentual, ou null se nenhuma faixa cobre a media
 */
export function resolverPercentualPorFaixa(media, faixas) {
  const candidatas = (faixas || []).filter(f => {
    const de = parseFloat(f.de);
    return !isNaN(de) && media >= de;
  });
  if (candidatas.length === 0) return null;

  const escolhida = candidatas.reduce((maior, atual) =>
    parseFloat(atual.de) > parseFloat(maior.de) ? atual : maior
  );
  return parseFloat(escolhida.crescimento) || 0;
}

/**
 * Monta media, meta e marcacao de piso para cada colaborador.
 *
 * Crescimento Fixo (faixas nulo/vazio): percentual unico para todos, como
 * sempre foi. Progressivo (faixas preenchido): cada colaborador usa o
 * percentual da faixa que cobre a PROPRIA media dele — por isso o percentual
 * aplicado tambem volta no resultado (percentual_aplicado), para a tela
 * mostrar de onde veio a meta de cada um.
 *
 * @param {Array<{id_colaborador: number, meses: Array}>} historico
 * @param {number|string} percentualCrescimento usado so quando faixas é nulo/vazio
 * @param {number|string} valorMinimo
 * @param {?Array<{de:number, ate:?number, crescimento:number}>} faixas shape interno do form, ver resolverPercentualPorFaixa
 * @returns {Array<{id_colaborador, media, meta_calculada, piso_aplicado, percentual_aplicado}>}
 */
export function calcularMetasColaboradores(historico, percentualCrescimento, valorMinimo, faixas = null) {
  const percentualFixo = parseFloat(percentualCrescimento) || 0;
  const piso = parseFloat(valorMinimo) || 0;
  const usaFaixas = Array.isArray(faixas) && faixas.length > 0;

  const pisoInteiro = Math.ceil(piso);

  return (historico || []).map(item => {
    const media = calcularMedia(item.meses);
    const percentual = usaFaixas ? resolverPercentualPorFaixa(media, faixas) : percentualFixo;
    const semPiso = metaComCrescimentoSemPiso(media, percentual || 0);
    return {
      id_colaborador: item.id_colaborador,
      media,
      meta_calculada: Math.max(semPiso, pisoInteiro),
      piso_aplicado: semPiso < pisoInteiro,
      percentual_aplicado: percentual
    };
  });
}
